import React from 'react';
import { CheckCircle, Loader2, Clock, AlertCircle, Calendar } from 'lucide-react';
import { useMyAttendance } from '../../hooks/useStudent';
import { getAttendanceStatusColor, getAttendanceStatusLabel } from '@/types/student.types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AttendanceTabProps {
  moduleElementId: string;
  // Past year = read-only consultation; absent = current year
  academicYearId?: string;
  // Former group's course instance; absent = the current group
  groupeId?: string;
}

const AttendanceTab: React.FC<AttendanceTabProps> = ({ moduleElementId, academicYearId, groupeId }) => {
  const { data: attendance, isLoading, error } = useMyAttendance(moduleElementId, academicYearId, groupeId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Erreur lors du chargement des présences
      </div>
    );
  }

  if (!attendance) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Aucune donnée de présence</h3>
        <p className="mt-2 text-sm text-gray-500">
          Les présences n'ont pas encore été enregistrées pour ce cours.
        </p>
      </div>
    );
  }

  // Get attendance rate color
  const getAttendanceRateColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PRESENT':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'LATE':
        return <Clock className="h-4 w-4 text-amber-600" />;
      case 'ABSENT_EXCUSED':
        return <AlertCircle className="h-4 w-4 text-blue-600" />;
      case 'ABSENT_UNEXCUSED':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  // A student who changed group mid-year sees sessions of both: name the group on each line
  const showGroups = new Set((attendance?.records ?? []).map((r) => r.groupName).filter(Boolean)).size > 1;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <CheckCircle className="h-5 w-5 text-blue-600" />
        Mes Présences
      </h2>

      {/* Summary Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Attendance Rate */}
          <div className="text-center">
            <div className={`text-3xl font-bold ${getAttendanceRateColor(attendance.attendanceRate)}`}>
              {attendance.attendanceRate}%
            </div>
            <p className="text-sm text-gray-500 mt-1">Taux de présence</p>
          </div>

          {/* Total Sessions */}
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{attendance.totalSessions}</div>
            <p className="text-sm text-gray-500 mt-1">Séances totales</p>
          </div>

          {/* Present */}
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{attendance.presentCount}</div>
            <p className="text-sm text-gray-500 mt-1">Présent</p>
          </div>

          {/* Late */}
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">{attendance.lateCount}</div>
            <p className="text-sm text-gray-500 mt-1">Retards</p>
          </div>

          {/* Absences */}
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {attendance.absentExcusedCount + attendance.absentUnexcusedCount}
            </div>
            <p className="text-sm text-gray-500 mt-1">Absences</p>
          </div>
        </div>

        {/* Breakdown */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4 justify-center text-sm">
          <span className="text-blue-600">
            {attendance.absentExcusedCount} absence{attendance.absentExcusedCount !== 1 ? 's' : ''} justifiée{attendance.absentExcusedCount !== 1 ? 's' : ''}
          </span>
          <span className="text-gray-400">•</span>
          <span className="text-red-600">
            {attendance.absentUnexcusedCount} absence{attendance.absentUnexcusedCount !== 1 ? 's' : ''} non justifiée{attendance.absentUnexcusedCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Attendance Records */}
      {attendance.records.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-600" />
              Historique des séances
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {attendance.records.map((record) => (
              <div
                key={record.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  {getStatusIcon(record.status)}
                  <div>
                    <div className="font-medium text-gray-900">
                      {format(new Date(record.date), 'EEEE d MMMM yyyy', { locale: fr })}
                    </div>
                    <div className="text-sm text-gray-500">
                      {record.startTime} - {record.endTime}
                      {showGroups && record.groupName && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                          {record.groupName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAttendanceStatusColor(record.status)}`}>
                    {getAttendanceStatusLabel(record.status)}
                  </span>
                  {record.lateMinutes && record.lateMinutes > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      +{record.lateMinutes} min de retard
                    </div>
                  )}
                  {record.justification && (
                    <div className="text-xs text-gray-500 mt-1 italic">
                      {record.justification}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          Aucune séance enregistrée pour le moment
        </div>
      )}

      {/* Warning if attendance is low */}
      {attendance.attendanceRate < 80 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
          <p>
            <strong>Attention :</strong> Votre taux de présence est inférieur à 80%.
            Un taux de présence insuffisant peut affecter votre évaluation.
          </p>
        </div>
      )}
    </div>
  );
};

export default AttendanceTab;
